'use strict';

/**
 * Medsyn Jitsi embed.
 *
 * Hardening goals (see docs/jitsi-embed-hardening.md):
 *   - tolerate modern Jitsi server config (prejoinConfig.enabled = true,
 *     disableDeepLinking = false) without relying solely on the legacy
 *     `prejoinPageEnabled` override
 *   - keep the SPA on the browser path on mobile (no app deep link), since
 *     the prompt comes from the Jitsi server, not from us
 *   - emit named diagnostic signals so we can tell prejoin / joined /
 *     media-error / browser-unsupported / connection-error apart from the
 *     browser console without reading Jitsi internals
 */

let mediaRecorders = [];
let chunks = [];
let currentDeviceId = "";
let localMediaRecorder;
let api;

const JITSI_LOG_PREFIX = "[medsyn-jitsi]";

function jitsiLog(state, payload) {
  // Single, greppable prefix for every state transition.
  // payload is optional — keep the call site cheap.
  if (payload === undefined) {
    console.log(JITSI_LOG_PREFIX, state);
  } else {
    console.log(JITSI_LOG_PREFIX, state, payload);
  }
  try {
    window.__MEDSYN_JITSI_STATE__ = {
      state: state,
      at: Date.now(),
      payload: payload === undefined ? null : payload
    };
  } catch (_) { /* window may be unavailable in tests */ }
}

// Cosmetic toast notifications we hide in the embed. Real errors
// (recording.failedToStart, connection.CONNFAIL, dialog.recording, ...) are
// intentionally NOT in this list — they must stay visible while HML is in
// homologation.
// Cosmetic notification keys we ask Jitsi to drop. The exact key for the
// "Moderador ativado" popup observed in HML is not pinned to a version, so
// we list every candidate the upstream codebase has used for moderator /
// moderation-mode / join-leave toasts. Errors (recording.*, connection.*,
// dialog.recording) are intentionally absent — they must stay visible.
const SUPPRESSED_NOTIFICATIONS = [
  // "You are the moderator now" — direct moderator toast
  "notify.moderator",
  "notify.grantedTo",
  "notify.grantedToUnknown",
  "notify.somebody",
  // Room-level audio/video moderation toggles. The pt-BR string for the
  // audio variant is "Moderação ativada" — likely match for the popup the
  // patient saw labelled "Moderador ativado".
  "notify.audioModerationEnabledTitle",
  "notify.audioModerationDisabledTitle",
  "notify.videoModerationEnabledTitle",
  "notify.videoModerationDisabledTitle",
  "notify.moderationStartedTitle",
  "notify.moderationStoppedTitle",
  "notify.moderationInEffectTitle",
  "notify.moderationInEffectDescription",
  // Join/leave member toasts (also covered by the deprecated
  // DISABLE_JOIN_LEAVE_NOTIFICATIONS interfaceConfig flag).
  "notify.connectedOneMember",
  "notify.connectedTwoMembers",
  "notify.connectedThreePlusMembers",
  "notify.leftOneMember",
  "notify.leftTwoMembers",
  "notify.leftThreePlusMembers",
  // Other benign state toasts that pollute the embed.
  "notify.raisedHand",
  "notify.linkToSalesforce",
  "notify.passwordSetRemotely",
  "notify.startSilentTitle",
  "notify.disableSelfView",
  "notify.allowedUnmute",
  "notify.mutedTitle"
];

function jitsiBaseConfigOverwrite(record) {
  // configOverwrite is applied on top of the server config.js. Keys here
  // intentionally mirror modern Jitsi names so the embed does not depend on
  // the server having legacy values. Both `prejoinPageEnabled` (legacy) and
  // `prejoinConfig.enabled` (current) are sent so any server version honours
  // the disable.
  return {
    startWithVideoMuted: false,
    startWithAudioMuted: false,
    apiLogLevels: ["error", "warn"],
    prejoinPageEnabled: false,
    prejoinConfig: { enabled: false },
    // Server may publish disableDeepLinking=false. We override here so the
    // embed never tries to hand off to the native app from inside the iframe.
    // The mobile "open in app" prompt the user sees is the server's, not ours.
    disableDeepLinking: true,
    // Skip the post-join feedback dialog — keeps the iframe in a single,
    // predictable terminal state when the call ends.
    requireDisplayName: false,
    enableClosePage: false,
    startSilent: false,
    // Hide the small crown overlay; product wants a clean embed.
    disableModeratorIndicator: true,
    disabledNotifications: SUPPRESSED_NOTIFICATIONS,
    // stable-9955 ships `recordings.requireConsent: true`, which renders the
    // RecordingConsentDialog after the IFrame API `startRecording` command
    // dispatches Jibri — blocking auto-start until the doctor clicks. Consent
    // is already collected by the Medsyn booking flow, so we disable the
    // in-call dialog. `skipConsentInMeeting` covers a parallel dynamic-branding
    // path that can re-enable it server-side.
    disableRecordAudioNotification: true,
    disabledSounds: ["RECORDING_ON_SOUND", "RECORDING_OFF_SOUND"],
    recordings: {
      recordAudioAndVideo: true,
      suggestRecording: record,
      showPrejoinWarning: false,
      requireConsent: false,
      skipConsentInMeeting: true
    }
  };
}

customElements.define("jitsi-custom-element",
  class extends HTMLElement {

    constructor() {
      super()
    }

    static get observedAttributes() {
      return ["domain", "room", "displayname", "jwt"]
    }

    connectedCallback() {
      this.init()
    }

    init() {
      const domain = this.getAttribute("domain")
      const room = this.getAttribute("room")
      const displayName = this.getAttribute("displayName")
      const jwt = this.getAttribute("jwt")

      if (typeof JitsiMeetExternalAPI !== "function") {
        // external_api.js is loaded inline from index.html before this bundle,
        // but a slow/blocked Jitsi origin can leave the global undefined.
        // Surface that as an explicit diagnostic instead of a generic
        // "construct of undefined" stack later on.
        jitsiLog("external-api-missing", { domain: domain });
        this.dispatchEvent(new CustomEvent("jitsiState", { detail: { state: "external-api-missing", domain: domain } }));
        return;
      }

      const record = false // window.location.href.indexOf("atendimento/revisando") != -1;
      const isDoctor = displayName === "Doctor";
      const baseOverwrite = jitsiBaseConfigOverwrite(isDoctor ? true : record);
      // The "recording" toolbar button is intentionally absent for all
      // profiles. Recording is fully managed by maybeStartRecording /
      // recordingHealthcheck below — there is no user-driven start/stop.
      // The doctor toolbar button used to be the only path that could
      // produce a `<jibri action='stop'>` IQ mid-call (one accidental
      // click ended a real consultation's recording on 2026-05-22).
      const toolbarButtons = ["microphone", "camera", "fodeviceselection"];
      const options = {
        roomName: room,
        userInfo: { displayName },
        configOverwrite: Object.assign({}, baseOverwrite, { toolbarButtons: toolbarButtons }),
        interfaceConfigOverwrite: {
          // Belt-and-suspenders: also disable the mobile redirect via the
          // legacy interfaceConfig key. Older Jitsi versions read it here.
          MOBILE_APP_PROMO: false,
          SHOW_JITSI_WATERMARK: false,
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
          DISABLE_PRESENCE_STATUS: true
        },
        parentNode: document.querySelector("jitsi-custom-element")
      };
      // The Elm contract reserves a `jwt` attribute but the backend does not
      // populate it yet. Forward to the iframe only when present so we don't
      // hand Jitsi an empty token (some versions reject that explicitly).
      if (jwt) {
        options.jwt = jwt;
      }

      jitsiLog("init", { domain: domain, room: room, displayName: displayName, mobile: !!window.isMobile });

      try {
        api = new JitsiMeetExternalAPI(domain, options);
      } catch (err) {
        jitsiLog("constructor-error", { message: err && err.message });
        this.dispatchEvent(new CustomEvent("jitsiState", { detail: { state: "constructor-error", message: err && err.message } }));
        return;
      }
      this.api = api
      let jitsiIFrame = document.querySelector("jitsi-custom-element").firstChild;
      jitsiIFrame.setAttribute('loading', 'eager');

      const self = this;
      const emitState = (state, detail) => {
        jitsiLog(state, detail);
        self.dispatchEvent(new CustomEvent("jitsiState", { detail: Object.assign({ state: state }, detail || {}) }));
      };

      // ---- Recording controller ------------------------------------------
      // Auto-start gates (all required):
      //   1) we are the doctor (only the doctor mediates)
      //   2) the local participant has a moderator-equivalent role
      //   3) at least one other human participant is in the room
      //   4) the MUC `<jibri-recording-status>` presence is not already `on`
      //
      // Gate (4) is the post-incident fix. Before it, every reload restarted
      // the JS state and re-issued `startRecording` even when Jibri was
      // already recording (s1 redundant start at 17:34:49 on 2026-05-22).
      //
      // Source of truth is the IFrame API `recordingStatusChanged` event,
      // which mirrors the MUC presence — never a local "I asked for start"
      // boolean. A local flag does not survive page reload; presence does.
      //
      // Policy (per product): missing a recording mid-call is unacceptable.
      // Multiple Jibri sessions per call are acceptable — the backend
      // finalizer concatenates them. So on `unknown` we err on the side of
      // starting after a short grace window for presence to arrive.
      let localParticipantId = null;
      let localIsModerator = false;
      let humanCount = 1; // local participant
      // Tracks every participant id ever observed as moderator in this call.
      // `muc_allowners` promotes the local user during MUC join, so the
      // `participantRoleChanged` IQ for the local id can arrive *before*
      // `videoConferenceJoined` sets `localParticipantId`. Recording the set
      // by id lets us replay it once we learn who we are.
      const moderatorIds = new Set();
      const MODERATOR_ROLES = { moderator: true, owner: true };

      // Authoritative recording state, fed exclusively by
      // `recordingStatusChanged` (i.e. by Jibri/Jicofo via XMPP). Never set
      // by client-side "I issued a start" optimism — that's how the bug
      // entered in the first place.
      let recordingPresence = "unknown"; // "unknown" | "on" | "off"
      let pendingStartTimer = null; // grace-period timer for "unknown"
      let recordingHealthcheck = null; // periodic re-evaluation while in call

      const UNKNOWN_GRACE_MS = 3000;
      const HEALTHCHECK_INTERVAL_MS = 20000;

      const isModeratorRole = role =>
        typeof role === "string" && MODERATOR_ROLES[role.toLowerCase()] === true;

      const issueStartRecording = reason => {
        jitsiLog("start-recording-issued", {
          reason: reason,
          humanCount: humanCount,
          recordingPresence: recordingPresence
        });
        // Extramed Jitsi exposes file recording via Jibri
        // (recordingService.enabled=true, liveStreaming.enabled=false).
        // Asking for mode "stream" triggers the embed error popup.
        api.executeCommand("startRecording", { mode: "file" });
      };

      const cancelPendingStart = () => {
        if (pendingStartTimer !== null) {
          clearTimeout(pendingStartTimer);
          pendingStartTimer = null;
        }
      };

      const maybeStartRecording = reason => {
        if (!isDoctor) return;
        if (!localIsModerator) {
          jitsiLog("start-recording-skipped", { reason: reason, why: "not-moderator-yet" });
          return;
        }
        if (humanCount < 2) {
          jitsiLog("start-recording-skipped", { reason: reason, why: "waiting-second-participant", humanCount: humanCount });
          return;
        }
        if (recordingPresence === "on") {
          jitsiLog("start-recording-skipped", { reason: reason, why: "already-on" });
          return;
        }
        if (recordingPresence === "off") {
          cancelPendingStart();
          issueStartRecording(reason);
          return;
        }
        // "unknown" — wait briefly for the first `recordingStatusChanged`
        // after join. If it arrives in time and reports "on" the timer is
        // cancelled in the event handler; if it reports "off" the same
        // handler will call us again with state "off". If nothing arrives,
        // start anyway (policy: prefer duplicate file over silent gap).
        if (pendingStartTimer !== null) {
          jitsiLog("start-recording-skipped", { reason: reason, why: "pending-grace" });
          return;
        }
        jitsiLog("start-recording-grace", { reason: reason, graceMs: UNKNOWN_GRACE_MS });
        pendingStartTimer = setTimeout(() => {
          pendingStartTimer = null;
          if (recordingPresence === "on") {
            jitsiLog("start-recording-skipped", { reason: "grace-expired", why: "already-on" });
            return;
          }
          issueStartRecording("grace-expired");
        }, UNKNOWN_GRACE_MS);
      };

      const startHealthcheck = () => {
        if (recordingHealthcheck !== null) return;
        if (!isDoctor) return;
        recordingHealthcheck = setInterval(() => {
          // Source of truth is presence. If Jibri died mid-call or a
          // server-side stop slipped past us, presence flips to "off" and
          // this re-issues the start. Idempotent against "on".
          maybeStartRecording("healthcheck");
        }, HEALTHCHECK_INTERVAL_MS);
        jitsiLog("recording-healthcheck-started", { intervalMs: HEALTHCHECK_INTERVAL_MS });
      };

      const stopHealthcheck = () => {
        if (recordingHealthcheck !== null) {
          clearInterval(recordingHealthcheck);
          recordingHealthcheck = null;
        }
        cancelPendingStart();
      };

      const seedLocalRoleFromApi = () => {
        if (typeof api.getParticipantsInfo !== "function") {
          jitsiLog("local-role-seed-skipped", { why: "api-missing" });
          return;
        }
        try {
          const info = api.getParticipantsInfo();
          if (!Array.isArray(info)) {
            jitsiLog("local-role-seed-skipped", { why: "info-not-array" });
            return;
          }
          const local = info.find(p => p && p.participantId === localParticipantId);
          if (!local) {
            jitsiLog("local-role-seed-skipped", {
              why: "local-not-in-snapshot",
              localParticipantId: localParticipantId,
              knownIds: info.map(p => p && p.participantId)
            });
            return;
          }
          if (!isModeratorRole(local.role)) {
            jitsiLog("local-role-seed-skipped", { why: "local-not-moderator", role: local.role });
            return;
          }
          localIsModerator = true;
          jitsiLog("local-role-seeded", { role: local.role });
          maybeStartRecording("seed");
        } catch (err) {
          jitsiLog("local-role-seed-error", { message: err && err.message });
        }
      };

      // Best-effort initial presence read. Some IFrame API versions expose
      // `isRecordingOn` or a Promise-returning equivalent; if available, the
      // first call short-circuits the 3s grace window post-reload. If
      // absent, we just wait for `recordingStatusChanged`.
      const seedRecordingPresenceFromApi = () => {
        const candidate = typeof api.isRecordingOn === "function"
          ? api.isRecordingOn()
          : (typeof api.getRecordingStatus === "function" ? api.getRecordingStatus() : null);
        if (candidate === null || candidate === undefined) {
          jitsiLog("recording-presence-seed-skipped", { why: "api-missing" });
          return;
        }
        Promise.resolve(candidate)
          .then(result => {
            const isOn = result === true
              || (result && (result.on === true
                || (typeof result.status === "string" && result.status.toLowerCase() === "on")));
            const next = isOn ? "on" : "off";
            if (recordingPresence === "unknown") {
              recordingPresence = next;
              jitsiLog("recording-presence-seeded", { state: next });
              app.ports.jitsiIsRecording.send(isOn);
              if (isOn) {
                cancelPendingStart();
              } else {
                maybeStartRecording("presence-seed");
              }
            }
          })
          .catch(err => {
            jitsiLog("recording-presence-seed-error", { message: err && err.message });
          });
      };

      // ---- Lifecycle / diagnostic events ----------------------------------
      // Modern Jitsi fires `prejoinScreenLoaded` whenever it lands on the
      // intermediate prejoin screen. The SPA used to assume we always went
      // straight into the room, which silently broke when the server enabled
      // prejoin. We log it explicitly so prejoin is no longer indistinguishable
      // from a hung connection.
      api.addEventListener("prejoinScreenLoaded", e => {
        emitState("prejoin", e);
      });

      api.addEventListener("readyToClose", e => {
        emitState("ready-to-close", e);
      });

      // `errorOccurred` covers media/connection errors uniformly on recent
      // Jitsi versions. Older deployments emit `connectionFailed` /
      // `conferenceError` instead — listen for both.
      api.addEventListener("errorOccurred", e => {
        emitState("error", e);
      });
      api.addEventListener("connectionFailed", e => {
        emitState("connection-error", e);
      });
      api.addEventListener("conferenceError", e => {
        emitState("conference-error", e);
      });
      api.addEventListener("suspendDetected", e => {
        emitState("suspend", e);
      });

      // ---- Existing wiring (unchanged port contract) ----------------------

      // It calls twice so I had to debounce it
      let debounce = 0
      api.addEventListener("videoConferenceLeft", e => {
        if (debounce == 1) {
          return
        }
        debounce = 1
        emitState("left", e);
        // Reset gates so a re-join inside the same iframe doesn't carry
        // stale moderator/recording state from the previous call.
        localParticipantId = null;
        localIsModerator = false;
        humanCount = 1;
        moderatorIds.clear();
        recordingPresence = "unknown";
        stopHealthcheck();
        this.dispatchEvent(new CustomEvent("videoConferenceLeft"))
        setTimeout(_ => {debounce = 0}, 1000)
      })

      api.addEventListener("participantJoined", e => {
        humanCount += 1;
        emitState("participant-joined", Object.assign({ humanCount: humanCount }, e || {}));
        // The legacy `grantModerator` call lived here and ran on the
        // non-doctor side with `e.Id` (wrong key, capital I). It cannot
        // bootstrap moderation from a non-moderator participant anyway, so
        // we removed it. If the product needs the doctor to be moderator,
        // the server must promote — either via JWT moderator claim or via
        // an anonymous MUC with `muc_allowners` (matching the legacy stack).
        maybeStartRecording("participant-joined");
        this.dispatchEvent(new CustomEvent("participantJoined", {detail: e}))
      })

      api.addEventListener("videoConferenceJoined", e => {
        localParticipantId = (e && (e.id || e.participantId)) || null;
        emitState("joined", e);
        // Replay any moderator promotion that arrived before we knew our id.
        // Observed in HML on stable-9955 with `muc_allowners`: the local
        // `participantRoleChanged` IQ fires before `videoConferenceJoined`,
        // so the id-equality check in that handler would always miss.
        if (localParticipantId && moderatorIds.has(localParticipantId) && !localIsModerator) {
          localIsModerator = true;
          jitsiLog("local-role-moderator-replayed", { localParticipantId: localParticipantId });
          maybeStartRecording("joined-replay");
        }
        // The server may have promoted the local user before any role-change
        // event fires (e.g. JWT moderator claim, MUC allowners). Query the
        // current snapshot so we don't sit forever waiting for an event that
        // never comes.
        seedLocalRoleFromApi();
        // Best-effort presence read for recording state — keeps the 3s
        // grace window from running when the IFrame API already knows.
        seedRecordingPresenceFromApi();
        // Periodic re-check: if Jibri stops or presence flips to "off"
        // mid-call without us noticing, the healthcheck re-issues start.
        // Idempotent against a current "on" state (maybeStartRecording
        // skips), so spinning this up unconditionally on join is safe.
        startHealthcheck();
        this.dispatchEvent(new CustomEvent("videoConferenceJoined", {detail: e}))
      })

      api.addEventListener("participantRoleChanged", e => {
        emitState("participant-role-changed", e);
        if (!e || !e.id) return;
        // Always track moderator state by id, even when we don't yet know
        // our own id. videoConferenceJoined will replay this set against the
        // local id once it's known.
        if (isModeratorRole(e.role)) {
          moderatorIds.add(e.id);
        } else {
          moderatorIds.delete(e.id);
        }
        if (e.id === localParticipantId && isModeratorRole(e.role)) {
          if (!localIsModerator) {
            localIsModerator = true;
            jitsiLog("local-role-moderator");
          }
          maybeStartRecording("role-changed");
        }
      })

      api.addEventListener("participantLeft", e => {
        humanCount = Math.max(1, humanCount - 1);
        emitState("participant-left", Object.assign({ humanCount: humanCount }, e || {}));
        this.dispatchEvent(new CustomEvent("participantLeft", {detail: e}))
      })

      api.addEventListener("deviceListChanged", e => {
        this.dispatchEvent(new CustomEvent("deviceListChanged", {detail: e}))
      })


      api.addEventListener("videoAvailabilityChanged", e => {
        api.isVideoMuted().then(muted => {
          api.getAvailableDevices().then(devices => {
            app.ports.jitsiVideoAvailable.send(!muted);
          })
        });

        this.dispatchEvent(new CustomEvent("videoAvailabilityChanged", {detail: e}))
      })

      api.addEventListener("audioAvailabilityChanged", e => {
        api.isAudioMuted().then(muted => {
          api.getAvailableDevices().then(devices => {
            app.ports.jitsiAudioAvailable.send(!muted);
          })
        });
        this.dispatchEvent(new CustomEvent("audioAvailabilityChanged", {detail: e}))
      })

      api.addEventListener("recordingStatusChanged", e => {
        emitState("recording-status", e);
        // Payload shape changed across Jitsi versions. The current docs
        // promise `{ on: boolean }`, older builds use `{ status: 'on' }`,
        // and a few in-between releases publish neither when the change
        // came from Jibri rather than from an explicit command. Normalize
        // to a strict boolean so the Elm side never receives `undefined`.
        const isOn = !!(e && (
          e.on === true
          || (typeof e.status === "string" && e.status.toLowerCase() === "on")
          || (typeof e.recordingState === "string" && e.recordingState.toLowerCase() === "on")
        ));
        app.ports.jitsiIsRecording.send(isOn);
        // Drive the authoritative recording-state machine. Presence is the
        // only signal we trust — anything else is local optimism and was
        // the original cause of the 2026-05-22 incident.
        recordingPresence = isOn ? "on" : "off";
        if (isOn) {
          // Presence confirmed "on" — cancel any pending grace-window
          // start so we don't fire a redundant `startRecording` IQ once
          // the timer expires.
          cancelPendingStart();
        } else if (e && e.error) {
          // Focus rejected our request (e.g. "not-allowed",
          // "service-unavailable" when Jibri is busy). Re-trying
          // immediately would tight-loop against the same rejection. Let
          // the healthcheck retry on its own cadence — 20s gives Jibri
          // time to free up and Jicofo time to settle.
          jitsiLog("recording-presence-error", { error: e.error });
        } else {
          // Clean "off" — Jibri stopped naturally (room emptied, server
          // restart). The doctor expects recording while in the room
          // with the proponent, so re-arm via the normal gate logic.
          // `maybeStartRecording` is idempotent and respects the gates.
          maybeStartRecording("presence-off");
        }
        api.isVideoMuted().then(muted => {
          if(muted){
            api.executeCommand('toggleVideo');
          }
        });
        api.isAudioMuted().then(muted => {
          if(muted){
            api.executeCommand('toggleAudio');
          }
        });
        this.dispatchEvent(new CustomEvent("recordingStatusChanged", {detail: e}))
      })

      api.addEventListener("browserSupport", e => {
        let supported = false;
        if (e.supported) {
          let userAgent = window.navigator.userAgent;
          if(/miui/i.test(userAgent) || /samsungbrowser/i.test(userAgent) ){
            // Stock MIUI/Samsung browsers historically fail Jitsi handshakes
            // even when Jitsi reports `supported: true`.
            jitsiLog("browser-unsupported-by-ua", { userAgent: userAgent });
            supported = false;
          } else {
            supported = e.supported;
          }
        } else {
          supported = e.supported;
        }
        emitState("browser-support", { supported: supported });
        app.ports.jitsiBrowserSupport.send(supported);
        this.dispatchEvent(new CustomEvent("browserSupport", {detail: e}))
      })

    }
  }
)
